import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { TrendingUp, AlertOctagon, ChevronRight } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'
import EpssBadge from './EpssBadge'

interface Mover {
  cve_id: string
  first_score: number
  last_score: number
  delta: number
  delta_pct: number
  slope_per_day: number
  forecast_in_window: number
  trending_high: boolean
  severity?: string
  cvss_score?: number | null
  kev?: boolean
  summary?: string
}

/** EPSS movers = CVEs whose exploit-probability score jumped meaningfully
 * over the past N days. Predictive: a CVE going 5% → 60% means actor research
 * is heating up *before* CISA flags KEV. Forecast column shows linear
 * extrapolation to "where this score will be in another window if pace holds". */
export default function EpssMovers({ windowDays = 7, limit = 12 }: { windowDays?: number; limit?: number }) {
  const [movers, setMovers] = useState<Mover[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let mounted = true
    setLoading(true)
    api.get('/api/cyber/epss-movers', { params: { window_days: windowDays, min_change: 0.05, limit } })
      .then(r => { if (mounted) setMovers(r.data?.movers || []) })
      .catch(() => { if (mounted) setMovers([]) })
      .finally(() => { if (mounted) setLoading(false) })
    return () => { mounted = false }
  }, [windowDays, limit])

  if (loading || movers.length === 0) return null

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2">
          <TrendingUp size={14} className="text-purple-300" /> EPSS movers — last {windowDays}d
          <span className="text-[10px] text-text-muted font-normal">
            forward-looking exploit-probability spikes · {movers.filter(m => m.trending_high).length} trending toward &gt;70%
          </span>
        </h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-text-muted border-b border-border">
              <th className="text-left py-2 px-2">CVE</th>
              <th className="text-right py-2 px-2">EPSS now</th>
              <th className="text-right py-2 px-2">Δ {windowDays}d</th>
              <th className="text-right py-2 px-2">Forecast +{windowDays}d</th>
              <th className="text-left py-2 px-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {movers.slice(0, limit).map(m => (
              <tr key={m.cve_id} className="border-b border-border/50 hover:bg-surface-hover">
                <td className="py-1.5 px-2">
                  <Link to={`/app/cve/${encodeURIComponent(m.cve_id)}`}
                    className="font-mono text-blue-light hover:underline flex items-center gap-1">
                    {m.cve_id}
                    {m.kev && <span className="badge-red text-[8px]">KEV</span>}
                  </Link>
                </td>
                <td className="py-1.5 px-2 text-right">
                  <EpssBadge score={m.last_score} variant="compact" />
                </td>
                <td className={clsx('py-1.5 px-2 text-right font-mono font-semibold',
                  m.delta > 0 ? 'text-rose-light' : m.delta < 0 ? 'text-emerald-light' : 'text-text-muted')}>
                  {m.delta > 0 ? '+' : ''}{m.delta_pct}pp
                </td>
                <td className="py-1.5 px-2 text-right">
                  <span className={clsx('font-mono inline-flex items-center gap-1',
                    m.trending_high ? 'text-rose font-bold' : 'text-text-secondary')}>
                    {m.trending_high && <AlertOctagon size={10} />}
                    {(m.forecast_in_window * 100).toFixed(0)}%
                  </span>
                </td>
                <td className="py-1.5 px-2 text-[10px] text-text-secondary truncate max-w-[300px]">
                  {m.summary || ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[9px] text-text-muted mt-2 italic">
        EPSS is FIRST.org's exploit-probability score (0-1). Bands per SANS ISC convention:
        critical ≥0.90 · high ≥0.50 · medium ≥0.10 · low &lt;0.10. Linear forecast assumes current pace continues.
      </p>
    </div>
  )
}
